import { z } from "zod";
//#region src/dp-recovery.ts
/**
* The answers of the five data plane recovery routes of provisioning-svc, as the operator client
* reads them (plan 2026-10-21-operator-tui, R-054e, F1):
*
* - `POST /v1/data-planes/:id/retry` answers {@link dpProvisioningJobSchema}.
* - `POST /v1/data-planes/:id/resume` answers {@link dpResumeResponseSchema}.
* - `POST /v1/data-planes/:id/restart-operator` and `POST /v1/data-planes/:id/force-upgrade`
*   answer {@link cpMessageResponseSchema}.
* - `POST /v1/data-planes/:id/execute-upgrade` answers {@link dpExecuteUpgradeResponseSchema}.
*
* The producer schemas in provisioning-svc `data-plane-response.dto.ts` type the server side and
* carry `Date` members. These schemas read the JSON wire form, where each date is an ISO text.
* Every object is strict, so an unknown key rejects the answer.
*/
/** The status of one provisioning step: the five values of `ProvisioningStepRecord.status`. */
const DP_PROVISIONING_STEP_STATUSES = [
	"pending",
	"running",
	"completed",
	"failed",
	"skipped"
];
/** One step of a provisioning job, as `ProvisioningStepRecord` stores it. Optional members are absent until set. */
const dpProvisioningStepSchema = z.object({
	name: z.string().min(1),
	label: z.string(),
	description: z.string(),
	status: z.enum(DP_PROVISIONING_STEP_STATUSES),
	startedAt: z.string().optional(),
	completedAt: z.string().optional(),
	durationMs: z.number().nonnegative().optional(),
	error: z.string().optional(),
	/**
	* Redacted output lines of the step. The producer type names a 64 KB cut, but no provisioning-svc
	* code writes this member: `runStep` sends each line on the event bus as `step_output` only.
	*/
	output: z.array(z.string()).optional()
}).strict();
/**
* One provisioning job, the answer of `retry` and the `job` of `resume`. For a workflow job this is
* the newest failed job, as provisioning-svc `resumeOperation` read it before it sent
* `RetryRequested`. For a `rotate` job it is the new rotate job that the resume inserted. `error`
* is the redacted failure text or `null`. `startedAt` and `completedAt` are `null` until the job
* reaches them.
*/
const dpProvisioningJobSchema = z.object({
	id: z.string().min(1),
	dataPlaneId: z.string().min(1),
	action: z.string().min(1),
	status: z.string().min(1),
	cloudProvider: z.string(),
	region: z.string(),
	steps: z.array(dpProvisioningStepSchema),
	error: z.string().nullable(),
	createdAt: z.iso.datetime(),
	startedAt: z.iso.datetime().nullable(),
	completedAt: z.iso.datetime().nullable()
}).strict();
/**
* The answer of `resume`: the job ({@link dpProvisioningJobSchema}), the operation it retries, and
* the last step that failed in the attempt before (`null` when no step failed).
*/
const dpResumeResponseSchema = z.object({
	job: dpProvisioningJobSchema,
	retrying: z.string().min(1),
	lastFailedStep: z.string().nullable()
}).strict();
/**
* A CP answer that carries one human text and no data: `restart-operator`, `force-upgrade` and
* the delete of a DCR redirect host. The text is for display only; no caller branches on it.
*/
const cpMessageResponseSchema = z.object({ message: z.string() }).strict();
/**
* The answer of `execute-upgrade`. provisioning-svc `executeUpgrade` answers `helmRevision: 0` on
* every success, so a caller must not read the member as a Helm revision.
*/
const dpExecuteUpgradeResponseSchema = z.object({ helmRevision: z.number().int().nonnegative() }).strict();
//#endregion
export { DP_PROVISIONING_STEP_STATUSES, cpMessageResponseSchema, dpExecuteUpgradeResponseSchema, dpProvisioningJobSchema, dpProvisioningStepSchema, dpResumeResponseSchema };

//# sourceMappingURL=dp-recovery.js.map